import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { IconEdit, IconEye } from '../../components/layout/icons';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { fetchPatients } from './patients.api';
import { PatientForm } from './PatientForm';
import { usePatients } from './patient.queries';
import type { Patient } from './types';

const calculateAge = (dateOfBirth: string) => {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) age -= 1;
  return age;
};

const calculateFullAge = (dateOfBirth: string) => {
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  let years = today.getFullYear() - birthDate.getFullYear();
  let months = today.getMonth() - birthDate.getMonth();
  let days = today.getDate() - birthDate.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  return `${years}Y ${months}M ${days}D`;
};

const formatGender = (gender: Patient['gender']) =>
  gender
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatAddress = (patient: Patient) =>
  [patient.address, patient.district, patient.state, patient.pinCode].filter(Boolean).join(', ') || '—';

const PatientRow = ({ patient, onEdit }: {
  patient: Patient;
  onEdit: (patient: Patient) => void;
}) => (
  <tr>
    <td>
      <div className="patient-name">
        <span>{patient.firstName.charAt(0)}{patient.lastName.charAt(0)}</span>
        <div><strong>{`${patient.firstName} ${patient.lastName}`.toUpperCase()}</strong><small>{patient.medicalRecordNumber}</small></div>
      </div>
    </td>
    <td>{calculateFullAge(patient.dateOfBirth)}</td>
    <td>{formatGender(patient.gender).toUpperCase()}</td>
    <td><span className="blood-badge">{patient.bloodGroup === 'unknown' ? '—' : patient.bloodGroup}</span></td>
    <td><div className="contact-cell"><span>{patient.phone}</span>{patient.email ? <small>{patient.email}</small> : null}</div></td>
    <td>{formatAddress(patient).toUpperCase()}</td>
    <td><span className={`status-badge ${patient.status}`}>{patient.status}</span></td>
    <td className="table-actions">
      <Link className="opd-row-action-icon" to={`/patients/${patient.id}`} title="View" aria-label="View patient">
        <IconEye />
      </Link>
      <button className="opd-row-action-icon" type="button" title="Edit" aria-label="Edit patient" onClick={() => onEdit(patient)}>
        <IconEdit />
      </button>
    </td>
  </tr>
);

export const PatientsPage = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search')?.trim() ?? '';
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState(initialSearch);
  const [search, setSearch] = useState(initialSearch);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const patientsQuery = usePatients(page, search, 10, sortBy, sortOrder);
  const tableSort = useTableSort(sortBy, sortOrder, (next) => {
    setSortBy(next.key);
    setSortOrder(next.direction);
    setPage(1);
  });

  const handleEdit = (patient: Patient) => {
    setEditingPatient(patient);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const applySearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const errorMessage = patientsQuery.error instanceof ApiError
    ? patientsQuery.error.message
    : 'Patient records could not be loaded.';

  const exportToExcel = async () => {
    const rows = await fetchAllPages<Patient>((exportPage, limit) => fetchPatients({
      page: exportPage,
      limit,
      ...(search ? { search } : {}),
      ...(sortBy ? { sortBy, sortOrder } : {}),
    }), 100);
    await exportRowsToExcel({
      fileName: `Patients-${new Date().toISOString().slice(0, 10)}`,
      title: 'Patients',
      subtitle: search ? `Search: "${search}"` : undefined,
      columns: [
        { header: 'SN', width: 6, align: 'right', map: (_row, index) => index + 1 },
        { header: 'MRN', width: 16, map: (row) => row.medicalRecordNumber },
        { header: 'Name', width: 24, map: (row) => `${row.firstName} ${row.lastName}`.toUpperCase() },
        { header: 'Age', width: 8, align: 'right', map: (row) => calculateAge(row.dateOfBirth) },
        { header: 'Gender', width: 12, map: (row) => formatGender(row.gender).toUpperCase() },
        { header: 'Blood Group', width: 12, map: (row) => row.bloodGroup === 'unknown' ? '-' : row.bloodGroup },
        { header: 'Phone', width: 14, map: (row) => row.phone },
        { header: 'Email', width: 22, map: (row) => row.email ?? '-' },
        { header: 'Address', width: 30, map: (row) => formatAddress(row).toUpperCase() },
        { header: 'Status', width: 12, map: (row) => row.status },
      ],
      rows,
    });
  };

  return (
    <div className="page-stack">
      <div className="page-heading">
        <div><p className="eyebrow">Clinical records</p><h1>Patients</h1><p>Find and manage patient records from one place.</p></div>
      </div>

      {editingPatient ? (
        <PatientForm
          key={editingPatient.id}
          patient={editingPatient}
          onCancel={() => setEditingPatient(null)}
          onSaved={() => setEditingPatient(null)}
        />
      ) : null}

      <section className="content-card">
        <div className="table-toolbar">
          <form className="search-box" onSubmit={applySearch} role="search">
            <span aria-hidden="true">⌕</span>
            <input
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search name, MRN, phone or email"
              aria-label="Search patients"
            />
            <button type="submit">Search</button>
          </form>
          <div className="table-toolbar-actions">
            <span className="result-count">{patientsQuery.data?.pagination.total ?? 0} records</span>
            <ExcelExportButton onExport={exportToExcel} disabled={!patientsQuery.isSuccess} />
          </div>
        </div>

        {patientsQuery.isLoading ? <LoadingState /> : null}
        {patientsQuery.isError ? (
          <ErrorState title="Unable to load patients" message={errorMessage} action={{ label: 'Try again', onClick: () => void patientsQuery.refetch() }} />
        ) : null}
        {patientsQuery.isSuccess && patientsQuery.data.data.length === 0 ? (
          <EmptyState
            title={search ? 'No matching patients' : 'No patients registered'}
            message={search ? 'Try a different name, MRN, phone number, or email.' : 'Patient records will appear here once they are registered.'}
            {...(search ? { action: { label: 'Clear search', onClick: () => { setSearch(''); setSearchInput(''); } } } : {})}
          />
        ) : null}
        {patientsQuery.isSuccess && patientsQuery.data.data.length > 0 ? (
          <>
            <div className="table-scroll">
              <table>
                <thead><tr><SortableTh control={tableSort} column="patient">Patient</SortableTh><SortableTh control={tableSort} column="age">Age</SortableTh><SortableTh control={tableSort} column="gender">Gender</SortableTh><SortableTh control={tableSort} column="bloodGroup">Blood</SortableTh><SortableTh control={tableSort} column="contact">Contact</SortableTh><SortableTh control={tableSort} column="address">Address</SortableTh><SortableTh control={tableSort} column="status">Status</SortableTh><th><span className="sr-only">Actions</span></th></tr></thead>
                <tbody>
                  {patientsQuery.data.data.map((patient) => (
                    <PatientRow key={patient.id} patient={patient} onEdit={handleEdit} />
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="button button-secondary" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>Previous</button>
              <span>Page {page} of {Math.max(patientsQuery.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={page >= patientsQuery.data.pagination.totalPages} onClick={() => setPage((value) => value + 1)}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};
